import { useEffect, useRef, useState } from "react";
import { ReactComponent as Bulb } from "@assets/images/bulb.svg";
import { ReactComponent as WhiteTick } from "@assets/images/WhiteTick.svg";
import { ReactComponent as GreenTick } from "@assets/images/greenTick.svg";
import { ReactComponent as Warning } from "@assets/images/warning.svg";
import { ReactComponent as InfoYellow } from "@assets/images/infoYellow.svg";
import BackImg from "@assets/images/stepsBack.jpg";
import { format, parse } from "date-fns";
import ButtonLoader from "@components/ui/Loaders/ButtonLoader";
import { toast } from "react-toastify";
import SleepCoachDoneDialogue from "@features/sleep/SleepCoachDoneDialogue";
import { useTranslation } from "react-i18next";
import { markSleepAsDone } from "api/sleepCoach";
import { ReactComponent as WhiteMoon } from "@assets/images/whiteMoon.svg";
import { getSleepCoachStepsData } from "data/uiData/CardsData/sleepData";
import OpenDescriptionCard from "@components/ui/OpenDescriptionCard";
import SleepOpenCards from "./SleepOpenCards";

const DashboardSuggestion = ({
  adviseData,
  todayAdviseData,
  handleSetData,
}) => {
  const { t } = useTranslation();
  const today = new Date().toISOString().split("T")[0];
  const todayName = new Date().toLocaleDateString("en-US", { weekday: "long" });
  const itemRefs = useRef([]);

  const todayIndex = (() => {
    const byDate = adviseData?.findIndex((item) => item.date === today);
    if (byDate !== -1) return byDate;

    const byDay = adviseData?.findIndex((item) => item.day === todayName);
    if (byDay !== -1) return byDay;

    return 0;
  })();

  const [tab, setTab] = useState("Weekly Summary");
  const [marAsDoneLoading, setMarkAsDoneLoading] = useState(false);
  // const todayIndex = adviseData.findIndex((item) => item.day === selectedDay) !==-1? adviseData.findIndex((item) => item.day === selectedDay) : 0

  const [activeIndex, setActiveIndex] = useState(todayIndex);
  const [open, setOpen] = useState(false);
  const days = adviseData?.map((item) => item.date);

  const activeDate =
    adviseData?.[activeIndex]?.date &&
    new Date(adviseData?.[activeIndex]?.date).toISOString().split("T")[0];
  const activeTodayDate =
    todayAdviseData?.date &&
    new Date(todayAdviseData?.date).toISOString().split("T")[0];
  const canMarkAsDone = activeDate && activeDate <= today;
  const canNotifShow = activeDate && activeDate < today;
  const canTodayMarkAsDone = activeTodayDate && activeTodayDate <= today;

  const handleMarkDone = async (data) => {
    setMarkAsDoneLoading(true);
    const activeItem = adviseData?.[activeIndex];
    if (!activeItem) return;

    const itemToMark =
      tab === "Weekly Summary" ? adviseData?.[activeIndex] : todayAdviseData;
    // Optimistic update
    try {
      const response = await markSleepAsDone({
        date: itemToMark?.date,
        wake_up: data?.wake_up,
        avg_sleep_duration: data?.avg_sleep_duration,
      });

      handleSetData(response);
      toast.info(t("toast.statusUpdated"));
    } catch (error) {
      console.error(error);
      toast.error(t("toast.failUpdated"));
    } finally {
      setMarkAsDoneLoading(false);
    }
  };

  const handleClose = (data = null) => {
    if (data) {
      handleMarkDone(data);
      setOpen(false);
    } else {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (itemRefs.current[activeIndex]) {
      itemRefs.current[activeIndex].scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [activeIndex]);

  const stepsData = getSleepCoachStepsData({
    sleepCoachStepsData:
      tab === "Weekly Summary"
        ? adviseData?.[activeIndex]?.description_steps
        : todayAdviseData?.description_steps,
    t,
  });

  function ShowWarning(date) {
    if (!date) return false;

    const inputDate = new Date(date);
    const today = new Date();

    // Normalize both to start of the day (ignore time)
    inputDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    return inputDate < today;
  }

  return (
    <div
      className="bg-white shadow-lg p-3 md:p-6 rounded-lg w-full max-w-full bg-cover h-[100%]"
      style={{ backgroundImage: `url(${BackImg})` }}
    >
      <div className="flex flex-col gap-2 md:flex-row justify-between items-center mb-4">
        <h2 className="font-[600] text-[#fff] text-[16px] font-[Lato]">
          {t("sleepCoachPreferences.Your Sleep Advice")}
        </h2>

        <div className="gird grid-cols-2 bg-white rounded-xl md:p-0 border border-[#E9E9E9]">
          {["Daily Advice", "Weekly Summary"].map((item) => {
            const isActive = tab === item;
            return (
              <button
                key={item}
                onClick={() => setTab(item)}
                className={`relative flex-1 px-6 py-2 rounded-xl capitalize transition-all duration-300 min-w-[120px] text-center ${
                  isActive
                    ? "text-white bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] "
                    : "bg-white text-black"
                }`}
              >
                <span className="flex items-center justify-center gap-2 relative text-[12px] font-[600]">
                  {/* {item} */}

                  {t(`sleepCoachPreferences.${item}`)}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {tab === "Weekly Summary" ? (
        <div className="border rounded-[17px] p-4 bg-white font-[Lato]">
          <div className="flex items-center rounded-[8px] md:gap-2 w-full border border-[2px] border-[#F2F2F2B8] overflow-x-auto bg-white">
            {days?.map((item, index) => {
              const isAllowed =
                ShowWarning(item) && !adviseData?.[index]?.mark_as_done;
              return (
                <div
                  key={index}
                  ref={(el) => (itemRefs.current[index] = el)}
                  className={`relative flex flex-col min-w-[80px] md:flex-1 rounded-[8px] py-[28px] md:px-[10px] cursor-pointer border border-white gap-[10px]
      ${
        activeIndex === index
          ? "bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-white"
          : "bg-white text-black hover:border-[#0575E6] hover:text-[#0575E6]"
      }
      ${
        !adviseData?.[index]?.mark_as_done &&
        isAllowed &&
        " border border-[#FBDB7D]"
      }
    `}
                  onClick={() => setActiveIndex(index)}
                >
                  {isAllowed && (
                    <div className="absolute top-2 right-2">
                      <Warning />
                    </div>
                  )}
                  <p className="text-[14px] font-[Lato] font-[400] text-center">
                    {t(
                      `dietPlanDashboard.allMeals.daysShort.${new Date(
                        item
                      ).toLocaleDateString("en-US", {
                        weekday: "short",
                      })}`
                    )}
                  </p>

                  <p className="text-[14px] font-[Lato] text-center">
                    {format(item, "dd MMM")}
                  </p>
                </div>
              );
            })}
          </div>
          {canNotifShow && !adviseData?.[activeIndex]?.mark_as_done && (
            <div className="bg-[#FFFFDD] border border-[#DFBE00] border-dashed border-[2px] rounded-full p-2 mt-2">
              <p className="text-[#DFBE00] text-[12px] font-[Lato] flex items-center gap-2">
                {" "}
                <InfoYellow />{" "}
                <span className="font-[600]">{t("Action Required")}:</span>
                {t(
                  "Complete this day’s check-in by clicking ‘Mark as done’ button to complete your progress."
                )}
              </p>
            </div>
          )}
          <div className="flex flex-row md:items-center justify-between gap-2 my-5">
            <div className="flex flex-col md:flex-row gap-2 md:items-center">
              <button className="text-[#fff] cursor-default text-[12px] md:text-[16px] font-[600] bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] rounded-full px-2 py-1 flex items-center gap-2 w-[fit-content]">
                <p className="bg-white rounded-full w-7 h-7 flex items-center justify-center">
                  <Bulb />
                </p>{" "}
                {new Date(adviseData?.[activeIndex]?.date).toDateString() ===
                new Date().toDateString()
                  ? t("TodayAdvice")
                  : `${t(
                      `days.${adviseData?.[activeIndex]?.day?.toLowerCase()}`
                    )} ’s ${t("Advice")}`}
              </button>

              {adviseData?.[activeIndex]?.per_night_wake_ups && (
                <>
                  <button className="text-[#fff] cursor-default text-[12px] md:text-[16px] font-[600] bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] rounded-full px-2 py-1 flex items-center gap-2 w-[fit-content]">
                    <p className="bg-white rounded-full w-7 h-7 flex items-center justify-center">
                      <WhiteMoon />
                    </p>{" "}
                    {t("sleepCoachPreferences.Nighttime awakenings")} :{" "}
                    {t(
                      `sleepCoachPreferences.${adviseData?.[activeIndex]?.per_night_wake_ups}`
                    )}
                  </button>
                </>
              )}
            </div>

            {canMarkAsDone &&
              (adviseData?.[activeIndex]?.mark_as_done ? (
                <button
                  className="text-[#fff] cursor-default text-[12px] md:text-[14px] border border-[#36F136] rounded-full px-2 py-1 flex items-center gap-2 w-[fit-content]"
                  // onClick={handleMarkDone}
                >
                  <p className="bg-white rounded-full w-7 h-7 flex items-center justify-center">
                    <GreenTick />
                  </p>
                </button>
              ) : (
                <button
                  className="text-[#fff] cursor-pointer text-[12px] md:text-[16px] font-[600] bg-[#36F136] rounded-full px-4 py-1 flex items-center justify-center gap-2 min-w-[136px] min-h-[36px]"
                  onClick={() => setOpen(true)}
                  disabled={marAsDoneLoading}
                >
                  {marAsDoneLoading ? (
                    <ButtonLoader />
                  ) : (
                    <>
                      <span>{t("sleepCoachPreferences.mark as done")}</span>
                      <WhiteTick />
                    </>
                  )}
                </button>
              ))}
          </div>
          <p className="text-[#000] font-[600] text-[18px] ">
            {adviseData[activeIndex]?.title}
          </p>

          <p className="text-[#7B7B7B] text-[18px]">
            {adviseData[activeIndex]?.description}
          </p>

          {adviseData?.[activeIndex]?.description_steps && (
            // <OpenDescriptionCard stepsData={stepsData} />
            <SleepOpenCards stepsData={stepsData} />
          )}
        </div>
      ) : (
        <div className="border rounded-[17px] p-4 bg-white font-[Lato]">
          <div className="flex flex-row md:items-center justify-between gap-2">
            <div className="flex flex-col md:flex-row gap-2 md:items-center">
              <button className="text-[#fff] cursor-default text-[12px] md:text-[14px] bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] rounded-full px-2 py-1 flex items-center gap-2 w-[fit-content] font-[600]">
                <p className="bg-white rounded-full w-7 h-7 flex items-center justify-center">
                  <Bulb />
                </p>{" "}
                {/* {t("sleepCoachPreferences.Weekly Advice")} */}
                {new Date(todayAdviseData?.date).toDateString() ===
                new Date().toDateString()
                  ? t("Today")
                  : `${t(`days.${todayAdviseData?.day?.toLowerCase()}`)}`}
                ’s
                {t("Advice")}
              </button>
              {todayAdviseData?.per_night_wake_ups && (
                <>
                  <button className="text-[#fff] cursor-default text-[12px] md:text-[16px] font-[600] bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] rounded-full px-2 py-1 flex items-center gap-2 w-[fit-content]">
                    <p className="bg-white rounded-full w-7 h-7 flex items-center justify-center">
                      <WhiteMoon />
                    </p>{" "}
                    {t("sleepCoachPreferences.Nighttime awakenings")} :{" "}
                    {t(
                      `sleepCoachPreferences.${todayAdviseData?.per_night_wake_ups}`
                    )}
                  </button>
                </>
              )}
            </div>
            {canTodayMarkAsDone &&
              (todayAdviseData?.mark_as_done ? (
                <button
                  className="text-[#fff] cursor-default text-[12px] md:text-[14px] border border-[#36F136] rounded-full px-2 py-1 flex items-center gap-2 w-[fit-content]"
                  // onClick={handleMarkDone}
                >
                  <p className="bg-white rounded-full w-7 h-7 flex items-center justify-center">
                    <GreenTick />
                  </p>
                </button>
              ) : (
                <button
                  className="text-[#fff] cursor-pointer text-[12px] md:text-[16px] font-[600] bg-[#36F136] rounded-full px-4 py-1 flex items-center justify-center gap-2 min-w-[136px] min-h-[36px]"
                  onClick={() => setOpen(true)}
                  disabled={marAsDoneLoading}
                >
                  {marAsDoneLoading ? (
                    <ButtonLoader />
                  ) : (
                    <>
                      {/* <p className="bg-white rounded-full w-7 h-7 flex items-center justify-center">
                      </p>{" "} */}
                      <span>{t("sleepCoachPreferences.mark as done")}</span>
                      <WhiteTick />
                    </>
                  )}
                </button>
              ))}
          </div>
          <p className="text-[#000] font-[500] text-[17px] mt-5">
            {todayAdviseData?.title}
          </p>

          <p className="text-[#7B7B7B] text-[16px]">
            {todayAdviseData?.description}
          </p>
          {todayAdviseData?.description_steps && (
            // <OpenDescriptionCard stepsData={stepsData} />
            <SleepOpenCards stepsData={stepsData} />
          )}
        </div>
      )}

      <SleepCoachDoneDialogue
        open={open}
        // handleClickOpen={handleClickOpen}
        handleClose={handleClose}
        isLoading={marAsDoneLoading}
        heading={t("sleepCoachPreferences.Daily Sleep Coach Update")}
        message={t(
          "sleepCoachPreferences.Track your average sleep duration and whether you woke up on time today."
        )}
      />
    </div>
  );
};

export default DashboardSuggestion;
